import { createClient, type Session as SupabaseSession, type User as SupabaseUser } from '@supabase/supabase-js';
import { getSafeRedirectPath } from '@/app/services/authHelpers';

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL!
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Lightweight client-side cache for auth session and user
let cachedSession: { value: SupabaseSession | null; timestamp: number } | null = null;
let cachedUser: { value: SupabaseUser | null; timestamp: number } | null = null;
let authSubscriptionInitialized = false;

function ensureAuthSubscriptionInitialized() {
  if (authSubscriptionInitialized) return;
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    // Invalidate caches whenever auth state changes
    cachedSession = { value: session ?? null, timestamp: 0 };
    cachedUser = { value: session?.user ?? null, timestamp: 0 };
  });
  // Best-effort: if the environment unsubscribes between hot reloads, ignore
  // Consumers of this module shouldn't need to manage this subscription
  authSubscriptionInitialized = Boolean(data?.subscription);
}

export function clearAuthCache() {
  cachedSession = null;
  cachedUser = null;
}

export async function cachedGetSession(ttlMs: number = 60_000): Promise<SupabaseSession | null> {
  ensureAuthSubscriptionInitialized();
  const now = Date.now();
  if (cachedSession && now - cachedSession.timestamp < ttlMs) {
    return cachedSession.value;
  }
  const { data: { session } } = await supabase.auth.getSession();
  cachedSession = { value: session ?? null, timestamp: now };
  return session ?? null;
}

export async function cachedGetUser(ttlMs: number = 60_000): Promise<SupabaseUser | null> {
  ensureAuthSubscriptionInitialized();
  const now = Date.now();
  if (cachedUser && now - cachedUser.timestamp < ttlMs) {
    return cachedUser.value;
  }
  const { data: { user } } = await supabase.auth.getUser();
  cachedUser = { value: user ?? null, timestamp: now };
  return user ?? null;
}

// Helper function for Google sign in
export const signInWithGoogle = async (redirectPath?: string) => {
  const safeRedirect = getSafeRedirectPath(redirectPath);
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(safeRedirect)}`,
      scopes: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/calendar.app.created'
    }
  });
  
  if (error) {
    console.error('Error signing in with Google:', error.message);
    throw error;
  }
  
  return data;
};

// Helper function to get Google access token from Supabase session
export const getGoogleAccessToken = async () => {
  const session = await cachedGetSession();
  if (!session) {
    throw new Error('No active session found');
  }
  if (!session.provider_token) {
    throw new Error('Please sign out and sign in with Google again.');
  }
  return session.provider_token;
};

export const signInWithMicrosoft = async () => {
  // Placeholder for Microsoft sign-in
  console.log("Signing in with Microsoft (not implemented)");
  return Promise.resolve();
};

export const signInWithApple = async () => {
  // Placeholder for Apple sign-in
  console.log("Signing in with Apple (not implemented)");
  return Promise.resolve();
};

// Helper function to get the current user
export const getCurrentUser = async () => {
  const user = await cachedGetUser();
  return user;
};

// Helper function to sign out
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  
  if (error) {
    console.error('Error signing out:', error.message);
    throw error;
  }
};
